"use client";

import React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import ServiceQuoteSection from "@/components/service/ServiceQuoteSection";
import ServiceFormSuccess from "@/components/service/ServiceFormSuccess";
import { inputClass, textareaClass, selectClass, labelClass, submitClass, formCardClass } from "@/components/service/formStyles";
function PowerWashingQuoteForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <ServiceFormSuccess title="Quote Request Sent" message={successMessage || "Thank you for your power washing enquiry! We'll contact you shortly with a free estimate."} onReset={resetForm} resetLabel="Request Another Quote" />;
  }
  return <Form onSubmit={handleSubmit} className={formCardClass}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="fullName" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClass}>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className={inputClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClass}>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="07700 900000" className={inputClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClass}>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className={inputClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="postcode" rules={{
        required: "Postcode is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClass}>Postcode</FormLabel>
              <FormControl>
                <Input placeholder="e.g. SO15 1AA" className={inputClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="serviceType" rules={{
        required: "Please select a service"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClass}>Service Required</FormLabel>
              <FormControl>
                <select {...field} className={selectClass}>
                  <option value="">Select service...</option>
                  <option value="power-washing">Power Washing</option>
                  <option value="driveway-cleaning">Driveway Cleaning</option>
                  <option value="patio-cleaning">Patio Cleaning</option>
                  <option value="commercial-cleaning">Commercial Cleaning</option>
                  <option value="fence-cleaning">Fence Cleaning</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="propertyType" rules={{
        required: "Please select property type"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClass}>Property Type</FormLabel>
              <FormControl>
                <select {...field} className={selectClass}>
                  <option value="">Select type...</option>
                  <option value="residential">Residential</option>
                  <option value="commercial">Commercial</option>
                  <option value="industrial">Industrial</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="projectDescription" rules={{
      required: "Please provide some details about the job"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelClass}>Project Details</FormLabel>
            <FormControl>
              <textarea placeholder="Please describe what needs cleaning, approximate size if known, and any specific issues like heavy staining or moss..." className={textareaClass} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 text-sm bg-destructive/10 text-destructive rounded-md border border-destructive/20">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className={submitClass}>
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Sending Request...
          </> : "Get Quote"}
      </Button>

      <p className="text-xs text-center text-muted-foreground mt-2">
        By submitting this form, you agree to be contacted regarding your enquiry. We respect your privacy.
      </p>
    </Form>;
}
export default function PowerWashingQuote() {
  return <ServiceQuoteSection id="get-a-free-quote" eyebrow="GET A FREE QUOTE" heading="Get Your Free Southampton Power Washing Quote" intro="Restore the beauty of your property with our professional power washing services. Fill out the form to request a free, accurate assessment and quote for your project in Southampton, Hampshire & surrounding areas." serviceName="Power Washing">
      <FormProvider formId="power-washing-quote-form" sectionName="Get Your Power Washing Quote" defaultValues={{
      fullName: "",
      email: "",
      phone: "",
      postcode: "",
      serviceType: "",
      propertyType: "",
      projectDescription: ""
    }} submitText="Get Quote" successMessage="Thank you for your power washing enquiry! We'll contact you shortly with a free estimate.">
        <PowerWashingQuoteForm />
      </FormProvider>
    </ServiceQuoteSection>;
}